import React, { useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

interface ChangePasswordProps {
  visible: boolean;
  onCancel: () => void;
}

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.success('密码修改成功');
        form.resetFields();
        onCancel();
      } else {
        message.error(data.error || '密码修改失败');
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      message.error('修改密码过程中发生错误');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // 如果是主账号，显示提示信息
  const isMainAccount = user?.isMainAccount;

  return (
    <Modal
      title="修改密码"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={500}
      destroyOnClose
      okText="确认修改"
      cancelText="取消"
      okButtonProps={{ disabled: isMainAccount }}
    >
      {isMainAccount ? (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          margin: '20px 0'
        }}>
          <LockOutlined style={{ fontSize: '24px', marginBottom: '10px' }} />
          <p>主账号密码无法通过此界面修改</p>
          <p style={{ fontSize: '12px' }}>请直接修改 .env 文件中的 ADMIN_PASSWORD 配置</p>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[
              { required: true, message: '请输入当前密码!' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入当前密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码!' },
              { min: 6, message: '密码至少6个字符!' },
              { max: 50, message: '密码最多50个字符!' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入新密码"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致!'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请再次输入新密码"
              autoComplete="new-password"
            />
          </Form.Item>

          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #bae7ff',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#666'
          }}>
            <p style={{ margin: 0 }}>密码要求：</p>
            <ul style={{ margin: '5px 0 0 20px', paddingLeft: 0 }}>
              <li>至少6个字符</li>
              <li>建议包含字母、数字和特殊字符</li>
            </ul>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default ChangePassword;
